import { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Filter } from 'lucide-react';
import { FilterField } from './FilterField';

interface FilterableQuestion {
  id: number;
  key: string;
  label: string;
  type: 'select' | 'multi_select' | 'radio' | 'string' | 'text';
  options?: Array<{ value: string; label: string }>;
}

interface FilterSidebarProps {
  filterableQuestions: FilterableQuestion[];
  activeFilters: Record<string, any>;
}

export function FilterSidebar({
  filterableQuestions,
  activeFilters,
}: FilterSidebarProps) {
  const [filters, setFilters] = useState<Record<string, any>>(activeFilters);
  const [isOpen, setIsOpen] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setFilters(activeFilters);
  }, [activeFilters]);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters };

    if (
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }

    setFilters(newFilters);
  };

  const applyFilters = () => {
    router.get(
      '/matches',
      { filters },
      {
        preserveState: false,
        preserveScroll: true,
      }
    );
  };

  const clearFilters = () => {
    setFilters({});
    router.get(
      '/matches',
      {},
      {
        preserveState: false,
        preserveScroll: true,
      }
    );
  };

  const removeFilter = (keyToRemove: string) => {
    const newFilters = { ...filters };
    delete newFilters[keyToRemove];
    setFilters(newFilters);

    // Apply the change immediately
    router.get(
      '/matches',
      { filters: newFilters },
      {
        preserveState: false,
        preserveScroll: true,
      }
    );
  };

  const activeFilterCount = Object.keys(filters).length;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg mb-6">
      {/* Header with toggle button */}
      <div className="flex justify-between items-center p-4">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="font-medium text-gray-900">Filters</h3>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount} active
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-sm"
            >
              Clear all
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            className="text-sm"
          >
            {isOpen ? 'Hide' : 'Show'} Filters
          </Button>
        </div>
      </div>

      {/* Collapsible filter content */}
      {isOpen && (
        <div className="border-t border-gray-200 p-4 bg-white">
          {/* Filter fields in a grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filterableQuestions.map((question) => (
              <FilterField
                key={question.id}
                question={question}
                value={filters[question.key]}
                onChange={(value) => handleFilterChange(question.key, value)}
              />
            ))}
          </div>

          {/* Apply button */}
          <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
            <Button
              onClick={applyFilters}
              disabled={
                JSON.stringify(filters) === JSON.stringify(activeFilters)
              }
              size="sm"
              className="px-6"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}

      {/* Active filters display when collapsed */}
      {!isOpen && activeFilterCount > 0 && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters).map(([key, value]) => {
              const question = filterableQuestions.find((q) => q.key === key);
              const label = question?.label || key;

              // For multi-select, show condensed version
              let displayValue = value;
              if (Array.isArray(value)) {
                displayValue =
                  value.length > 2
                    ? `${value.slice(0, 2).join(', ')}... (+${value.length - 2})`
                    : value.join(', ');
              }

              return (
                <Badge key={key} variant="secondary" className="pl-2 pr-1 py-1">
                  <span className="text-xs">
                    <span className="font-medium">{label}:</span> {displayValue}
                  </span>
                  <button
                    onClick={() => removeFilter(key)}
                    className="ml-2 p-1 hover:bg-gray-300 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
